document.addEventListener('DOMContentLoaded', () => {
  if (window.catchyVariantPickerInitialized) return;
  window.catchyVariantPickerInitialized = true;
  
  const variantsJson = document.querySelector('[data-product-variants]');
  if (!variantsJson) return;

  const variants = JSON.parse(variantsJson.textContent);

  const picker = document.querySelector('.custom-variant-picker');
  const hasEdition = picker?.dataset.hasEdition === 'true';

  const STOKKE_HIGH_CHAIR = 'STOKKE Tripp Trapp';
  const ORIGINAL_EDITION = 'The Original';
  const PREMIUM_EDITION = 'The Premium';
  const OTHER_HIGH_CHAIR = 'Other';

  let shouldScrollToGallery = false;
  let lastPremiumState = null;

  /*
   * ---------------------------------------
   * Helpers
   * ---------------------------------------
   */

  // Format prices in the active market's currency (the variant JSON is in
  // minor units of the presentment currency). Mirrors byob-builder.js.
  let moneyFormatter = null;
  try {
    moneyFormatter = new Intl.NumberFormat(picker?.dataset.locale || undefined, {
      style: 'currency',
      currency: picker?.dataset.currency || 'AUD',
    });
  } catch (e) {
    /* fall back to plain formatting below */
  }

  function formatMoney(cents) {
    const amount = cents / 100;
    return moneyFormatter ? moneyFormatter.format(amount) : `$${amount.toFixed(2)}`;
  }

  function getSelectedHighChair() {
    return document.querySelector('.custom-option--high-chair input:checked')?.value;
  }

  function getSelectedEdition() {
    const fieldset = getEditionFieldset();

    if (!fieldset) return null;

    return (
      fieldset.querySelector('input[type="radio"]:checked')?.value || null
    );
  }

  function getSelectedColor() {
    return document.querySelector('.custom-option--color input:checked')?.value;
  }

  function getEditionFieldset() {
    return document.querySelector('.custom-option--edition');
  }

  function getColorFieldset() {
    return document.querySelector('.custom-option--color');
  }

  function isStokke(highChair) {
    return highChair === 'STOKKE Tripp Trapp';
  }

  function showPremiumOptions() {
    return picker?.dataset.showPremiumOptions === 'true';
  }

  /*
   * ---------------------------------------
   * Edition funnel analytics
   * ---------------------------------------
   * Measures whether shoppers who reach the edition choice actually
   * consider Premium, or default straight through on Original:
   *   edition_picker_shown       - denominator (Stokke selected, both editions visible)
   *   edition_why_premium_click  - opened the "Why Premium?" detail popup
   *   edition_selected           - chose an edition (params.edition)
   * "Never considered Premium" = shown minus anyone who fired either of the other two.
   * Mirrors the destinations byob-builder.js already sends to.
   */

  function readCookie(name) {
    const m = document.cookie.match('(?:^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function sendGA4(name, params) {
    const mid = picker?.dataset.ga4Id;
    const secret = picker?.dataset.ga4Secret;
    if (!mid || !secret) return;

    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed() === false) {
        return;
      }
    } catch (e) {
      /* proceed if the API isn't present */
    }

    const ga = readCookie('_ga');
    if (!ga) return;
    const gp = ga.split('.');
    const clientId = gp.length >= 4 ? gp[2] + '.' + gp[3] : ga;

    const gs = readCookie('_ga_' + mid.replace('G-', ''));
    const sessionId = gs ? gs.split('.')[2] : undefined;

    const p = { engagement_time_msec: 1 };
    if (sessionId) p.session_id = sessionId;
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) p[k] = params[k];
    });

    try {
      fetch(
        'https://www.google-analytics.com/mp/collect?measurement_id=' +
          encodeURIComponent(mid) + '&api_secret=' + encodeURIComponent(secret),
        {
          method: 'POST',
          keepalive: true,
          body: JSON.stringify({ client_id: clientId, events: [{ name: name, params: p }] }),
        }
      );
    } catch (e) {
      /* analytics failure must never affect the storefront */
    }
  }

  function trackEdition(name, payload) {
    const data = { source: 'edition-picker', chair: getSelectedHighChair() || null, ...payload };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...data });
    } catch (e) {
      /* no GTM/GA4 */
    }
    try {
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(name, data);
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', name, data]);
      }
    } catch (e) {
      /* no Klaviyo */
    }
    sendGA4(name, data);
  }

  let editionPickerShownTracked = false;

  function trackEditionPickerShown() {
    if (editionPickerShownTracked) return;
    editionPickerShownTracked = true;
    trackEdition('edition_picker_shown', {});
  }

  /*
   * ---------------------------------------
   * Visibility of dependent options
   * ---------------------------------------
   * Rules:
   * - Edition only for STOKKE Tripp Trapp
   * - Color for any selected High Chair
   */

  function updateDependentOptions() {
    const selectedHighChair = document.querySelector(
      '.custom-option--high-chair input[type="radio"]:checked'
    );

    const editionFieldset = getEditionFieldset();
    const colorFieldset = getColorFieldset();
    const klaviyoWrapper = document.querySelector('.catchy-klaviyo-wrapper');
    const buyButtons = document.querySelector('.buy-buttons-block');

    // Color fieldset is required
    if (!colorFieldset) return;

    // Hide everything first
    editionFieldset?.classList.remove('is-visible');
    colorFieldset.classList.remove('is-visible');
    klaviyoWrapper?.classList.add('hidden');
    buyButtons?.classList.remove('hidden');

    // Handle "Other"
    if (isOtherHighChairSelected) {
      klaviyoWrapper?.classList.remove('hidden');
      buyButtons?.classList.add('hidden');
      return;
    }

    if (!selectedHighChair) return;

    const value = selectedHighChair.value.trim();

    // Any supported high chair selected => show colors
    colorFieldset.classList.add('is-visible');

    // Reset edition when it is not applicable
    if (
      editionFieldset &&
      (value !== STOKKE_HIGH_CHAIR || !showPremiumOptions())
    ) {
      const originalRadio = editionFieldset.querySelector(
        `input[value="${ORIGINAL_EDITION}"]`
      );

      if (originalRadio && !originalRadio.checked) {
        originalRadio.checked = true;
        originalRadio.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      }
    }

    // Show Edition only if the fieldset exists
    if (
      editionFieldset &&
      value === STOKKE_HIGH_CHAIR &&
      showPremiumOptions()
    ) {
      editionFieldset.classList.add('is-visible');
      trackEditionPickerShown();
    }

    updateColorTabsVisibility();
  }

  /*
   * ---------------------------------------
   * Variant lookup
   * ---------------------------------------
   * Assumption:
   * option1 = High Chair
   * option2 = Edition
   * option3 = Color
   */

  function getSelectedVariant() {
    const highChair = getSelectedHighChair();
    const edition = getSelectedEdition();
    const color = getSelectedColor();

    if (!highChair || !color) return null;

    return (
      variants.find((variant) => {
        if (hasEdition) {
          return (
            variant.option1 === highChair &&
            variant.option2 === edition &&
            variant.option3 === color
          );
        }

        return (
          variant.option1 === highChair &&
          variant.option2 === color
        );
      }) || null
    );
  }

  /*
   * ---------------------------------------
   * Context variants for "From $xx.xx"
   * ---------------------------------------
   * If a full variant is not selected yet,
   * show the minimum price in the current
   * High Chair / Edition context.
   */

  function getContextVariants() {
    const highChair = getSelectedHighChair();
    const edition = getSelectedEdition();

    if (!highChair) return [];

    if (isStokke(highChair)) {
      // If STOKKE but edition not selected yet,
      // use all STOKKE variants
      if (!edition) {
        return variants.filter(
          (variant) => variant.option1 === highChair
        );
      }

      return variants.filter(
        (variant) =>
          variant.option1 === highChair &&
          variant.option2 === edition
      );
    }

    // Non-STOKKE
    return variants.filter(
      (variant) => variant.option1 === highChair
    );
  }

  /*
   * ---------------------------------------
   * Price updater
   * ---------------------------------------
   * - Full variant selected => exact price
   * - Otherwise => From $xx.xx
   */

  function updateCatchyPrice() {
    const priceEl = document.querySelector('.catchy-price');
    const price = priceEl?.querySelector('.catchy-price__price');
    const compare = priceEl?.querySelector('.catchy-price__compare');

    if (!priceEl || !price || !compare) return;

    const selectedVariant = getSelectedVariant();

    // Variant selected
    if (selectedVariant) {
      price.textContent = formatMoney(selectedVariant.price);

      if (
        selectedVariant.compare_at_price &&
        selectedVariant.compare_at_price > selectedVariant.price
      ) {
        compare.textContent = formatMoney(selectedVariant.compare_at_price);
        compare.classList.remove('hidden');
      } else {
        compare.textContent = '';
        compare.classList.add('hidden');
      }

      return;
    }

    const contextVariants = getContextVariants();

    // No variants in current context
    if (!contextVariants.length) {
      price.textContent = '';
      compare.textContent = '';
      compare.classList.add('hidden');
      return;
    }

    const availableContextVariants = contextVariants.filter(
      (variant) => variant.available
    );

    const variantsToUse = availableContextVariants.length
      ? availableContextVariants
      : contextVariants;

    const minPrice = Math.min(
      ...variantsToUse.map((variant) => variant.price)
    );

    const fromLabel = picker?.dataset.fromLabel || 'From';
    price.textContent = `${fromLabel} ${formatMoney(minPrice)}`;

    // Hide compare price in "From" state
    compare.textContent = '';
    compare.classList.add('hidden');
  }

  function getVariantIdInput() {
    // The page can contain more than one input[name="id"] (installments
    // form, quick-add forms) — resolve the one inside the ATC form.
    const addToCartButton = getAddToCartButton();
    const productForm = addToCartButton ? addToCartButton.closest('form') : null;

    return (
      (productForm && productForm.querySelector('input[name="id"]')) ||
      document.querySelector('input[name="id"]')
    );
  }

  let lastVariantId = null;

  async function updateSelectedVariant() {
    const variant = getSelectedVariant();
    const variantIdInput = getVariantIdInput();

    if (variant && variantIdInput) {
      variantIdInput.value = variant.id;
    }

    updateCatchyPrice();
    updateStockInfo();

    // Search cleared / no valid variant selected
    if (!variant) {
      if (lastVariantId !== null) {
        lastVariantId = null;

        document.dispatchEvent(
          new CustomEvent('catchy:variant-change', {
            detail: {
              variant: null
            }
          })
        );
      }

      updateAddToCartState();
      return;
    }

    // Variant changed
    if (variant.id !== lastVariantId) {
      lastVariantId = variant.id;

      document.dispatchEvent(
        new CustomEvent('catchy:variant-change', {
          detail: {
            variant
          }
        })
      );

      if (shouldScrollToGallery) {
        shouldScrollToGallery = false;

        setTimeout(() => {
          scrollToGallery();
        }, 100);
      }
    }

    updateAddToCartState();
  }



  /*
   * ---------------------------------------
   * Selected color label
   * ---------------------------------------
   */

  function updateSelectedColorLabel() {
    const selectedColorInput = document.querySelector(
      '.custom-option--color input:checked'
    );

    const selectedColorWrapper = document.querySelector('.selected-color');
    const colorNameEl = document.querySelector('.selected-color .color-name');

    if (!selectedColorWrapper || !colorNameEl) return;

    if (!selectedColorInput) {
      colorNameEl.textContent = '';
      selectedColorWrapper.style.display = 'none';
      return;
    }

    colorNameEl.textContent = selectedColorInput.value;
    selectedColorWrapper.style.display = '';
  }

  /*
   * ---------------------------------------
   * Tab filtering
   * ---------------------------------------
   * We use:
   * - is-hidden => color doesn't exist for current selection
   * - is-disabled => sold out
   * - is-tab-hidden => hidden because current tab doesn't match
   */

  function applyActiveColorTabFilter() {
    const activeTab = document.querySelector('.custom-color-tab.is-active');
    const activeCategory = activeTab?.dataset.category || 'all';

    document.querySelectorAll('.custom-color-swatch').forEach((swatch) => {
      if (
        activeCategory === 'all' ||
        swatch.dataset.category === activeCategory
      ) {
        swatch.classList.remove('is-tab-hidden');
      } else {
        swatch.classList.add('is-tab-hidden');
      }
    });
  }

  function updateColorTabs() {
    const tabs = document.querySelectorAll('.custom-color-tab');
    const activeTab = document.querySelector('.custom-color-tab.is-active');

    let activeTabStillVisible = true;

    tabs.forEach((tab) => {
      const category = tab.dataset.category;

      // Always keep All visible
      if (category === 'all') {
        tab.style.display = '';
        return;
      }

      const visibleSwatches = document.querySelectorAll(
        `.custom-color-swatch[data-category="${category}"]:not(.is-hidden)`
      );

      const visible = visibleSwatches.length > 0;

      tab.style.display = visible ? '' : 'none';

      if (activeTab === tab && !visible) {
        activeTabStillVisible = false;
      }
    });

    // If active tab is now empty, switch back to All
    if (!activeTabStillVisible) {
      const allTab = document.querySelector(
        '.custom-color-tab[data-category="all"]'
      );

      if (allTab) {
        document
          .querySelectorAll('.custom-color-tab')
          .forEach((item) => item.classList.remove('is-active'));

        allTab.classList.add('is-active');
      }
    }

    applyActiveColorTabFilter();
  }

  function updateColorTabsVisibility() {
    const colorTabs = document.querySelector('.custom-color-tabs');
    const highChair = getSelectedHighChair();
    const edition = getSelectedEdition();

    if (!colorTabs) return;

    let shouldHide = false;

    if (!highChair) {
        shouldHide = true;
    } else if (isStokke(highChair) && edition === PREMIUM_EDITION) {
        shouldHide = true;
    }

    if (shouldHide) {
        colorTabs.style.display = 'none';

        // reset active tab to All
        const allTab = document.querySelector('.custom-color-tab[data-category="all"]');
        if (allTab) {
        document
            .querySelectorAll('.custom-color-tab')
            .forEach((item) => item.classList.remove('is-active'));

        allTab.classList.add('is-active');
        applyActiveColorTabFilter();
        }

        return;
    }

    colorTabs.style.display = '';
  }

  /*
   * ---------------------------------------
   * Color availability
   * ---------------------------------------
   * Behavior:
   * - Hide colors that don't exist for current selection
   * - Disable sold-out colors
   * - Auto-select first available color if current becomes invalid
   */

  function getPremiumColors() {
    //const picker = document.querySelector('.custom-variant-picker');
    if (!picker) return [];

    try {
      return JSON.parse(picker.dataset.premiumColors || '[]').map((value) =>
        String(value).trim().toLowerCase()
      );
    } catch (error) {
      return [];
    }
  }

  function updateColorAvailability() {
    const highChair = getSelectedHighChair();
    const edition = getSelectedEdition();
    const hasEdition = !!getEditionFieldset();

    const colorSwatches = document.querySelectorAll(
      '.custom-option--color .custom-color-swatch'
    );

    if (!highChair) {
      colorSwatches.forEach((swatch) => {
        const radio = swatch.querySelector('input');
        swatch.classList.add('is-hidden');
        swatch.classList.remove('is-disabled', 'is-tab-hidden');

        if (radio) {
          radio.disabled = false;
          radio.checked = false;
        }
      });

      updateColorTabs();
      updateSelectedColorLabel();
      updateSelectedVariant();
      return;
    }

    const premiumAllowedColors = getPremiumColors();

    // Premium mode only matters for STOKKE + Premium
    const premiumMode =
      showPremiumOptions() &&
      isStokke(highChair) &&
      edition === PREMIUM_EDITION;

    // Original mode should hide premium colors for:
    // - STOKKE + The Original
    // - any non-STOKKE high chair
    const hidePremiumColors =
      (isStokke(highChair) && edition === ORIGINAL_EDITION) ||
      !isStokke(highChair);

    let firstAvailableRadio = null;

    colorSwatches.forEach((swatch) => {
      const radio = swatch.querySelector('input');
      if (!radio) return;

      const colorValue = radio.value;
      const normalizedColor = colorValue.trim().toLowerCase();

      let matchingVariants = [];

      if (hasEdition) {
        if (isStokke(highChair)) {
          if (!edition) {
            matchingVariants = variants.filter((variant) => {
              return (
                variant.option1 === highChair &&
                variant.option3 === colorValue
              );
            });
          } else {
            matchingVariants = variants.filter((variant) => {
              return (
                variant.option1 === highChair &&
                variant.option2 === edition &&
                variant.option3 === colorValue
              );
            });
          }
        } else {
          matchingVariants = variants.filter((variant) => {
            return (
              variant.option1 === highChair &&
              variant.option3 === colorValue
            );
          });
        }
      } else {
        // Product has no Edition option
        matchingVariants = variants.filter((variant) => {
          return (
            variant.option1 === highChair &&
            variant.option2 === colorValue
          );
        });
      }

      const exists = matchingVariants.length > 0;
      const available = matchingVariants.some((variant) => variant.available);

      swatch.classList.remove('is-hidden', 'is-disabled');
      radio.disabled = false;

      /*
      * Rule 1:
      * If STOKKE Premium is selected, show only premium colors
      */
      if (premiumMode && !premiumAllowedColors.includes(normalizedColor)) {
        swatch.classList.add('is-hidden');

        if (radio.checked) {
          radio.checked = false;
        }

        return;
      }

      /*
      * Rule 2:
      * Hide premium colors for:
      * - STOKKE Original
      * - all non-STOKKE high chairs
      */
      if (hidePremiumColors && premiumAllowedColors.includes(normalizedColor)) {
        swatch.classList.add('is-hidden');

        if (radio.checked) {
          radio.checked = false;
        }

        return;
      }

      /*
      * Rule 3:
      * Hide colors that do not exist for current selection
      */
      if (!exists) {
        swatch.classList.add('is-hidden');

        if (radio.checked) {
          radio.checked = false;
        }

        return;
      }

      /*
      * Rule 4:
      * Mark sold-out colors but keep them selectable
      */
      if (!available) {
        swatch.classList.add('is-disabled');
        radio.disabled = false;

        if (!firstAvailableRadio) {
          firstAvailableRadio = radio;
        }

        return;
      }

      if (!firstAvailableRadio) {
        firstAvailableRadio = radio;
      }
    });

    const selectedColor = document.querySelector(
      '.custom-option--color input:checked'
    );

    if (!selectedColor && firstAvailableRadio) {
      firstAvailableRadio.checked = true;
      firstAvailableRadio.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }

    updateColorTabs();
    updateSelectedColorLabel();
    updateSelectedVariant();
  }

  function getHighChairFieldset() {
    return document.querySelector('.custom-option--high-chair');
  }

  function getHighChairSearchInput() {
    return document.querySelector('.custom-high-chair__search');
  }

  function getHighChairError() {
    return document.querySelector('.custom-high-chair-error');
  }

  function showHighChairError() {
    const errorEl = getHighChairError();
    const fieldset = getHighChairFieldset();

    if (errorEl) {
      errorEl.hidden = false;
    }

    if (fieldset) {
      fieldset.classList.add('has-error');
    }
  }

  function hideHighChairError() {
    const errorEl = getHighChairError();
    const fieldset = getHighChairFieldset();

    if (errorEl) {
      errorEl.hidden = true;
    }

    if (fieldset) {
      fieldset.classList.remove('has-error');
    }
  }

  function validateHighChairSelection() {
    const selectedHighChair = getSelectedHighChair();
    const searchInput = getHighChairSearchInput();
    const inputValue = searchInput ? searchInput.value.trim() : '';

    // Must have a checked radio AND the input text must exactly match it
    if (!selectedHighChair || inputValue !== selectedHighChair) {
      showHighChairError();
      return false;
    }

    hideHighChairError();
    return true;
  }

  // Show/hide original/premium blocks
  function updateEditionDependentContent() {
    const highChair = getSelectedHighChair();
    const edition = getSelectedEdition();

    const originalBlocks = document.querySelectorAll('.block-original');
    const premiumBlocks = document.querySelectorAll('.block-premium');

    const isPremium =
      showPremiumOptions() &&
      highChair === STOKKE_HIGH_CHAIR &&
      edition === PREMIUM_EDITION;

    if (lastPremiumState !== isPremium) {
      lastPremiumState = isPremium;

      document.dispatchEvent(
        new CustomEvent('catchy:premium-change', {
          detail: {
            isPremium
          }
        })
      );
    }

    // Default state
    originalBlocks.forEach((block) => block.classList.remove('hidden'));
    premiumBlocks.forEach((block) => block.classList.add('hidden'));

    // No chair selected -> keep default
    if (!highChair) return;

    // Non-STOKKE chairs -> keep Original content
    if (!isStokke(highChair)) return;
    

    // STOKKE + Premium
    if (
      showPremiumOptions() &&
      edition === PREMIUM_EDITION
    ) {
      originalBlocks.forEach((block) => block.classList.add('hidden'));
      premiumBlocks.forEach((block) => block.classList.remove('hidden'));
      return;
    }

    // STOKKE + Original (or no edition yet)
    originalBlocks.forEach((block) => block.classList.remove('hidden'));
    premiumBlocks.forEach((block) => block.classList.add('hidden'));
    
  }

  function scrollToGallery() {
    if (window.innerWidth > 749) return;

    const gallery = document.querySelector('.product-custom-gallery');
    if (!gallery) return;

    const stickyHeader = document.querySelector('.header__row--top');
    const headerHeight = stickyHeader ? stickyHeader.offsetHeight : 0;

    const y =
      gallery.getBoundingClientRect().top +
      window.pageYOffset -
      headerHeight -
      12; // Optional 12px spacing below the header

    window.scrollTo({
      top: y,
      behavior: 'smooth'
    });
  }

  /*
   * ---------------------------------------
   * High Chair autocomplete
   * ---------------------------------------
   */

  document.querySelectorAll('.custom-high-chair').forEach((picker) => {
    const searchInput = picker.querySelector('.custom-high-chair__search');
    const resultsBox = picker.querySelector('.custom-high-chair__results');
    const results = picker.querySelectorAll('.custom-high-chair__result');

    if (!searchInput || !resultsBox || !results.length) return;

    const fuseItems = [...results]
      .filter(item => item.dataset.value !== OTHER_HIGH_CHAIR)
      .map(item => ({
        text: item.dataset.searchText,
        element: item
      }));

    const fuse = new Fuse(fuseItems, {
      keys: ['text'],
      threshold: 0.6,
      location: 0,
      distance: 100,
      minMatchCharLength: 1
    });

    let highlightedIndex = -1;

    function getVisibleResults() {
      return [...results].filter(
        (item) => item.style.display !== 'none'
      );
    }

    function updateHighlight() {
      const visibleResults = getVisibleResults();

      visibleResults.forEach((item) =>
        item.classList.remove('is-highlighted')
      );

      if (
        highlightedIndex >= 0 &&
        highlightedIndex < visibleResults.length
      ) {
        visibleResults[highlightedIndex].classList.add('is-highlighted');
        visibleResults[highlightedIndex].scrollIntoView({
          block: 'nearest'
        });
      }
    }    

    function selectResult(result) {
      const selectedValue = result.dataset.value;
      const typedValue = searchInput.value.trim();

      searchInput.value = selectedValue;

      shouldScrollToGallery = true;

      // Track whether "Other" is selected
      isOtherHighChairSelected = selectedValue === OTHER_HIGH_CHAIR;

      // Copy typed text to Klaviyo before replacing it with "Other"
      if (isOtherHighChairSelected) {
        setKlaviyoHighChairModel(typedValue);
      }

      // Clear any previous radio selection
      picker.querySelectorAll('input[data-search-value]').forEach((input) => {
        input.checked = false;
      });

      if (!isOtherHighChairSelected) {
        const radio = picker.querySelector(
          `input[data-search-value="${CSS.escape(selectedValue)}"]`
        );

        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      }

      hideHighChairError();

      resultsBox.classList.remove('active');

      updateDependentOptions();

      if (isOtherHighChairSelected) {
        setKlaviyoHighChairModel(typedValue);
      }

      updateColorTabsVisibility();
      updateEditionDependentContent();
      updateColorAvailability();
      updateSelectedVariant();
    }

    function setKlaviyoHighChairModel(value) {
      requestAnimationFrame(() => {
        const input = document.querySelector(
          'input[id^="High_Chair_Model_Requested_"]'
        );

        if (!input) return;

        input.value = value;

        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('blur', { bubbles: true }));
      });
    }

    searchInput.addEventListener('focus', function () {
      const selectedValue = searchInput.value.trim();

      results.forEach((item) => {
        item.style.display = 'block';

        if (item.dataset.value === OTHER_HIGH_CHAIR) {
          item.classList.remove('show-result');
        } else {
          item.classList.add('show-result');
        }
      });

      const visibleResults = getVisibleResults();

      const selectedItem = visibleResults.find(
        (item) => item.dataset.value === selectedValue
      );

      if (selectedItem) {
        highlightedIndex = visibleResults.indexOf(selectedItem);
      } else {
        highlightedIndex = -1;
      }

      updateHighlight();

      resultsBox.classList.add('active');

      // Scroll to the input when focused
      if (window.innerWidth <= 749) {
        this.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }

      // Select current value so typing replaces it
      requestAnimationFrame(() => this.select());
    });

    let galleryReset = false;

    searchInput.addEventListener('input', () => {
      const term = searchInput.value.toLowerCase().trim();
      let hasResults = false;

      // Hide all results except "Other"
      results.forEach((item) => {
        if (item.dataset.value === OTHER_HIGH_CHAIR) {
          item.style.display = 'block';
          item.classList.remove('show-result');
        } else {
          item.style.display = 'none';
          item.classList.remove('show-result');
        }
      });

      if (term === '') {
        // Show everything when search is empty
        results.forEach((item) => {
          item.style.display = 'block';
          item.classList.add('show-result');
        });

        hasResults = true;
      } else {
        const matches = fuse.search(term);

        matches.forEach(({ item }) => {
          item.element.style.display = 'block';
          item.element.classList.add('show-result');
        });

        hasResults = matches.length > 0;
      }

      // If user edits the text away from the selected radio value, uncheck it
      const checkedRadio = picker.querySelector('input[type="radio"]:checked');
      if (checkedRadio && searchInput.value.trim() !== checkedRadio.value) {
        checkedRadio.checked = false;
      }

      isOtherHighChairSelected = false;

      // Reset gallery only once when search is cleared
      if (term === '') {
        if (!galleryReset) {
          galleryReset = true;

          document.dispatchEvent(
            new CustomEvent('catchy:variant-change', {
              detail: {
                variant: null
              }
            })
          );
        }
      } else {
        galleryReset = false;
      }

      highlightedIndex = -1;
      updateHighlight();

      resultsBox.classList.toggle(
        'active',
        hasResults || term !== ''
      );

      updateDependentOptions();
      updateColorAvailability();
      updateSelectedVariant();
    });

    results.forEach((result) => {
      result.addEventListener('click', () => {
        selectResult(result);
      });
    });

    searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }

      const visibleResults = getVisibleResults();
      if (!visibleResults.length) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          highlightedIndex = Math.min(
            highlightedIndex + 1,
            visibleResults.length - 1
          );
          updateHighlight();
          break;

        case 'ArrowUp':
          event.preventDefault();
          highlightedIndex = Math.max(
            highlightedIndex - 1,
            0
          );
          updateHighlight();
          break;

        case 'Enter':
          if (highlightedIndex >= 0) {
            event.preventDefault();
            selectResult(visibleResults[highlightedIndex]);
          }
          break;

        case 'Escape':
          resultsBox.classList.remove('active');
          highlightedIndex = -1;
          updateHighlight();
          break;
      }
    });
  });

  //Add to cart button helpers
  function getAddToCartButton() {
    return document.querySelector('button[name="add"]');
  }

  function getProductFormButtons() {
    return document.querySelector('.product-form-buttons');
  }

  function getAddToCartValidationLayer() {
    return document.querySelector('.custom-atc-validation-layer');
  }

  function isHighChairSelectionValid() {
    const selectedHighChair = getSelectedHighChair();
    const searchInput = getHighChairSearchInput();
    const inputValue = searchInput ? searchInput.value.trim() : '';

    return !!selectedHighChair && inputValue === selectedHighChair;
  }

  function hasAvailableVariantForSelectedHighChair() {
    const highChair = getSelectedHighChair();
    if (!highChair) return false;

    return variants.some((variant) => {
      return variant.option1 === highChair && variant.available;
    });
  }

  function updateAddToCartState() {
    const addToCartButton = getAddToCartButton();
    const validationLayer = getAddToCartValidationLayer();
    const priceEl = document.querySelector('.catchy-price');

    if (!addToCartButton) return; 

    const isHighChairValid = isHighChairSelectionValid();
    const hasAvailableVariant = hasAvailableVariantForSelectedHighChair();

    // const selectedVariant = getSelectedVariant();
    // const isSelectionComplete = !!selectedVariant && selectedVariant.available;

    // const shouldDisable = !isHighChairValid || !isSelectionComplete;

    const selectedVariant = getSelectedVariant();    

    const hasVariant = !!selectedVariant;
    const isSoldOut = hasVariant && !selectedVariant.available;

    const shouldDisable =
      !isHighChairValid ||
      !hasVariant ||
      isSoldOut;

    addToCartButton.disabled = shouldDisable;
    addToCartButton.setAttribute('aria-disabled', String(shouldDisable));

    const buttonText = addToCartButton.querySelector('span');

    if (buttonText) {
      if (isSoldOut) {
        buttonText.textContent = Theme.translations.sold_out_text;
      } else {
        buttonText.textContent = Theme.translations.add_to_cart_text;
      }
    }

    if (validationLayer) {
      validationLayer.classList.toggle('is-active', !isHighChairValid);
    }

    if (priceEl) {
      priceEl.style.display = hasAvailableVariant ? '' : 'none';
    }
  }

  function getLowStockThreshold() {
    //const picker = document.querySelector('.custom-variant-picker');
    if (!picker) return 0;

    const value = Number(picker.dataset.lowStockThreshold);
    return Number.isNaN(value) ? 0 : value;
  }

  function updateStockInfo() {
    const lowStockEl = document.querySelector('.low-stock-info');
    const inStockEl = document.querySelector('.in-stock-info');

    if (!lowStockEl && !inStockEl) return;

    const variant = getSelectedVariant();

    // reset both
    lowStockEl?.classList.add('hidden');
    inStockEl?.classList.add('hidden');

    // no fully selected variant
    if (!variant) return;

    // unavailable variant => show nothing
    if (!variant.available) return;

    const threshold = getLowStockThreshold();

    // inventory not tracked => show in stock
    if (!variant.inventory_management) {
      inStockEl?.classList.remove('hidden');
      return;
    }

    const qty = Number(variant.inventory_quantity);

    // missing quantity => fall back to in stock
    if (Number.isNaN(qty)) {
      inStockEl?.classList.remove('hidden');
      return;
    }

    if (qty > 0 && qty <= threshold) {
      lowStockEl?.classList.remove('hidden');
    } else {
      inStockEl?.classList.remove('hidden');
    }
  }

  // Close autocomplete on outside click
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.custom-high-chair')) {
      document
        .querySelectorAll('.custom-high-chair__results')
        .forEach((el) => el.classList.remove('active'));
    }
  });

  /*
   * ---------------------------------------
   * Edition change
   * ---------------------------------------
   */

  document.querySelectorAll('.custom-option--edition input').forEach((input) => {
    input.addEventListener('change', () => {
      updateColorTabsVisibility();
      updateEditionDependentContent();
      updateColorAvailability();
      updateSelectedVariant();
    });
  });

  // Only count a deliberate click, not the programmatic reset back to Original
  // that runs whenever the chair selection changes.
  document.querySelectorAll('.custom-option--edition .custom-edition-card').forEach((card) => {
    card.addEventListener('click', () => {
      const edition = card.querySelector('input[type="radio"]')?.value;
      if (edition) trackEdition('edition_selected', { edition });
    });
  });

  document
    .querySelector('.custom-edition-legend__link .popup-link__button')
    ?.addEventListener('click', () => {
      trackEdition('edition_why_premium_click', {});
    });

  /*
   * ---------------------------------------
   * Color change
   * ---------------------------------------
   */

  document.querySelectorAll('.custom-option--color input').forEach((input) => {
    input.addEventListener('change', () => {
      updateSelectedColorLabel();
      updateSelectedVariant();
    });
  });

  /*
   * ---------------------------------------
   * Color category tabs
   * ---------------------------------------
   */

  document.querySelectorAll('.custom-color-tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      document
        .querySelectorAll('.custom-color-tab')
        .forEach((item) => item.classList.remove('is-active'));

      tab.classList.add('is-active');
      applyActiveColorTabFilter();
    });
  });

  //Add to cart error if high chair is not selected
  const validationLayer = getAddToCartValidationLayer();

  if (validationLayer) {
    validationLayer.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      showHighChairError();

      const highChairFieldset = getHighChairFieldset();
      if (highChairFieldset) {
        highChairFieldset.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    });
  }

  //Hide error on radio change
  document
    .querySelectorAll('.custom-option--high-chair input[type="radio"]')
    .forEach((input) => {
      input.addEventListener('change', () => {
        hideHighChairError();
        updateAddToCartState();
      });
    });

  //Close mobile high chair popup
  document.querySelector('.custom-high-chair__close')?.addEventListener('click', () => {
    document.querySelector('.custom-high-chair__results')?.classList.remove('active');
  });

  /*
   * ---------------------------------------
   * Initial sync on page load
   * ---------------------------------------
   */

  //updateDependentOptions();
  //updateColorAvailability();
  //updateSelectedColorLabel();
  //updateSelectedVariant();
  updateAddToCartState();

});